import QRCode from "qrcode";
import { API } from "./api";

const esc = (s) =>
  (s ?? "").toString().replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const galleryUrl = (path, id) => `${API}/gallery/${path}/${id}`;
const dlUrl = (a) => `${API}/files/${a.path}?download=1&filename=${encodeURIComponent(a.filename || "")}`;
const isImg = (a) => (a.content_type || "").startsWith("image/") || a.kind === "photo";

// Opens a clean report in a new tab. It does NOT auto-print — the user can print or
// "Save as PDF" from the toolbar button. Photos are downloadable; the QR code (linking to
// the public gallery) is only rendered on the printed page, not on screen.
export async function printRecords({ title, subtitle = "", path, records = [], columns = [] }) {
  const withAtt = records.filter((r) => (r.attachments || []).length);
  const qr = {};
  await Promise.all(
    withAtt.map(async (r) => {
      try {
        qr[r.id] = await QRCode.toDataURL(galleryUrl(path, r.id), { width: 160, margin: 1 });
      } catch {
        qr[r.id] = "";
      }
    })
  );

  const headRow = columns.map((c) => `<th>${esc(c.label)}</th>`).join("");
  const bodyRows =
    records
      .map((r) => {
        const cells = columns.map((c) => `<td>${esc(c.value(r))}</td>`).join("");
        const n = (r.attachments || []).length;
        return `<tr>${cells}<td>${n || ""}</td></tr>`;
      })
      .join("") ||
    `<tr><td colspan="${columns.length + 1}" style="text-align:center;color:#999;padding:24px">No records</td></tr>`;

  const attBlocks = withAtt
    .map((r) => {
      const label = columns
        .slice(0, 2)
        .map((c) => esc(c.value(r)))
        .filter(Boolean)
        .join(" · ");
      const imgs = (r.attachments || [])
        .filter(isImg)
        .map((a) => `<a class="thumb" href="${dlUrl(a)}" download title="Download ${esc(a.filename)}"><img src="${API}/files/${a.path}" /><span>&#8681; Download</span></a>`)
        .join("");
      const docs = (r.attachments || [])
        .filter((a) => !isImg(a))
        .map((a) => `<a href="${dlUrl(a)}" download>${esc(a.filename)}</a>`)
        .join(", ");
      return `<div class="att">
        <div class="att-head">${label || "Record"}</div>
        <div class="att-body">
          <div class="thumbs">${imgs || '<span class="muted">No photos</span>'}${
        docs ? `<div class="docs">Docs: ${docs}</div>` : ""
      }</div>
          ${qr[r.id] ? `<div class="qr"><img src="${qr[r.id]}"/><span>Scan for photos</span></div>` : ""}
        </div>
      </div>`;
    })
    .join("");

  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
    body{font-family:Arial,Helvetica,sans-serif;color:#111;padding:24px}
    .toolbar{position:sticky;top:0;background:#16265F;margin:-24px -24px 20px;padding:12px 24px;display:flex;justify-content:space-between;align-items:center}
    .toolbar span{color:#fff;font-weight:bold;font-size:13px}
    .toolbar button{background:#E0A81E;color:#16265F;border:0;font-weight:bold;text-transform:uppercase;letter-spacing:.05em;font-size:12px;padding:9px 18px;cursor:pointer}
    h1{font-size:18px;margin:0 0 2px} .meta{color:#666;font-size:12px;margin-bottom:16px}
    table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:8px}
    th{text-align:left;text-transform:uppercase;letter-spacing:.05em;font-size:10px;color:#555;border-bottom:2px solid #111;padding:6px 8px}
    td{padding:6px 8px;border-bottom:1px solid #ddd;vertical-align:top}
    tr:nth-child(even) td{background:#f7f7f7}
    h2{font-size:14px;border-top:2px solid #111;padding-top:12px;margin:22px 0 8px}
    .att{border:1px solid #ddd;padding:10px;margin-bottom:12px;page-break-inside:avoid}
    .att-head{font-weight:bold;font-size:13px;margin-bottom:8px}
    .att-body{display:flex;justify-content:space-between;gap:16px;align-items:flex-start}
    .thumbs{display:flex;flex-wrap:wrap;gap:10px}
    .thumb{display:inline-block;text-decoration:none;color:#16265F;font-size:10px;font-weight:bold;text-align:center}
    .thumb img{height:120px;width:auto;object-fit:cover;border:1px solid #ccc;display:block;margin-bottom:2px}
    .docs{font-size:11px;color:#333;margin-top:6px;flex-basis:100%}
    .qr{text-align:center;font-size:10px;color:#555;flex-shrink:0}
    .qr img{width:120px;height:120px;display:block;margin:0 auto 4px}
    .muted{color:#999;font-size:11px}
    a{color:#111}
    @media print{ .toolbar{display:none} .thumb span{display:none} }
    @media screen{ .qr{display:none} }
  </style></head><body>
    <div class="toolbar">
      <span>Whipp Sales &amp; Service — ready to print or save as PDF</span>
      <button onclick="window.print()">Print / Save as PDF</button>
    </div>
    <h1>${esc(title)}</h1>
    <div class="meta">${records.length} record(s)${subtitle ? " · " + esc(subtitle) : ""} · ${esc(
    new Date().toLocaleString()
  )}</div>
    <table><thead><tr>${headRow}<th>Files</th></tr></thead><tbody>${bodyRows}</tbody></table>
    ${withAtt.length ? `<h2>Attachments</h2>${attBlocks}` : ""}
  </body></html>`);
  w.document.close();
  w.focus();
}
